import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, inject } from "@angular/core"
import { IconImageService } from "@shared/services/icon-image.service"
import { AgColor, AgSize, AgType } from "@shared/components/flow-badge-v2/flow-badge-v2.component"
import { AgTextDirective } from "@shared/directives/ag-text.directive"

@Component({
  selector: "flow-chip",
  templateUrl: "./flow-chip.component.html",
  styleUrl: "./flow-chip.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlowChipComponent implements AfterViewChecked {
  iconService = inject(IconImageService)
  private changeDetector = inject(ChangeDetectorRef)

  @ViewChild("label") private labelRef?: ElementRef<HTMLElement>

  // whether the label is cut off, and so worth a tooltip holding the full text
  public truncated = false

  @Input() text: string = ""
  @Input() color: AgColor = "Neutral"
  @Input() size: AgSize = "Small"
  @Input() type: AgType = "Solid"
  @Input() icon: string = ""
  @Input() disabled: boolean = false
  @Output() close = new EventEmitter<void>()

  public onClose(): void {
    if (this.disabled) return
    this.close.emit()
  }

  // the label is ellipsised by CSS, so the full text is only worth a tooltip when it does not
  // fit. Measured after the view is checked, because the widths only mean anything once the
  // browser has laid the label out - reading them while rendering can report a stale size.
  ngAfterViewChecked(): void {
    this.syncTruncated()
  }

  // the same text can start fitting or stop fitting when the row is resized, and no input
  // has changed at that point, so the measurement has to be repeated
  @HostListener("window:resize")
  public onWindowResize(): void {
    this.syncTruncated()
  }

  private syncTruncated(): void {
    const label = this.labelRef?.nativeElement
    if (!label) return

    const truncated = label.scrollWidth > label.clientWidth
    // guarded so the flag settles after one flip instead of re-triggering the view check
    if (truncated === this.truncated) return

    this.truncated = truncated
    this.changeDetector.detectChanges()
  }
}

export { AgColor, AgSize, AgType }
